/** Entwurfs-Autosave für den Vertrags-Wizard.
 *
 *  Warum überhaupt: ein angefangener Pflegevertrag ging beim Neuladen der Seite
 *  verloren. Wie bei Angeboten und Rechnungen in Kontor gilt jetzt — sobald ein
 *  Kunde feststeht, existiert ein echter Entwurf in der Datenbank, und jede
 *  weitere Änderung wird still nachgeschrieben. Dadurch taucht der Anlauf auch
 *  unter „Verträge" auf, statt nur im Kopf des Bearbeiters zu existieren.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export type SpeicherStand = "ruht" | "wartet" | "speichert" | "gespeichert" | "fehler";

/**
 * Ruft `speichern` verzögert auf, wenn sich `wert` ändert.
 *
 * Zwei Eigenschaften, ohne die es nicht trägt:
 * - **Zusammenfassen.** Beim Tippen entstünde pro Tastendruck ein Aufruf. Erst
 *   `verzoegerungMs` nach der letzten Änderung wird geschrieben.
 * - **Kein paralleler Lauf.** Läuft noch ein Schreibvorgang, wird der nächste
 *   danach nachgeholt — sonst überholen sich zwei Aufrufe und der ältere Stand
 *   gewinnt.
 */
export function useAutoSave<T>(
  wert: T,
  speichern: (w: T) => Promise<unknown>,
  { aktiv = true, verzoegerungMs = 1200 }: { aktiv?: boolean; verzoegerungMs?: number } = {},
) {
  const [stand, setStand] = useState<SpeicherStand>("ruht");
  const laeuft = useRef(false);
  const offen = useRef<T | null>(null);
  const zuletzt = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Die Funktion darf sich bei jedem Render ändern, ohne den Timer neu zu starten.
  const fnRef = useRef(speichern);
  fnRef.current = speichern;

  const lauf = useCallback(async () => {
    if (laeuft.current || offen.current === null) return;
    const w = offen.current;
    offen.current = null;
    laeuft.current = true;
    setStand("speichert");
    try {
      await fnRef.current(w);
      setStand("gespeichert");
    } catch {
      setStand("fehler");
    } finally {
      laeuft.current = false;
      if (offen.current !== null) void lauf();
    }
  }, []);

  useEffect(() => {
    if (!aktiv) return;
    const fingerabdruck = JSON.stringify(wert);
    // Erster Durchlauf nach dem Laden: Stand merken, aber nicht schreiben —
    // sonst schriebe jedes Öffnen eines Entwurfs ihn ungefragt zurück.
    if (zuletzt.current === null) { zuletzt.current = fingerabdruck; return; }
    if (zuletzt.current === fingerabdruck) return;
    zuletzt.current = fingerabdruck;
    offen.current = wert;
    setStand("wartet");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void lauf(), verzoegerungMs);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [wert, aktiv, verzoegerungMs, lauf]);

  /** Sofort schreiben, was noch aussteht (vor dem Verlassen der Seite). */
  const jetztSpeichern = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    await lauf();
  }, [lauf]);

  return { stand, jetztSpeichern };
}

export const SPEICHER_TEXT: Record<SpeicherStand, string> = {
  ruht: "",
  wartet: "…",
  speichert: "speichert …",
  gespeichert: "Entwurf gespeichert",
  fehler: "Nicht gespeichert — Verbindung prüfen",
};
